import React, { useState, useEffect } from 'react';
// Remove direct Firebase imports
// import { db } from '../../firebase/firebase';
// import { doc, updateDoc, Timestamp } from "firebase/firestore";
import { Modal, Form, Button, Alert, Row, Col, Badge } from 'react-bootstrap';
import { Pencil, Save, XCircle, CheckCircle, Clock, Ban } from 'react-bootstrap-icons';
import './UserEditModal.css';
// Add UserService import
import { services } from '../../Services/AdminService';

const UserEditModal = ({ 
  show, 
  onHide, 
  user, 
  onUserUpdated,
  availablePlans = []
}) => {
  // Get UserService instance
  const userService = services.users;
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'basic',
    customer_id: '',
    status: 'pending',
    phone: '',
    company: '',
    designation: '',
    notes: '',
    tags: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);

  // Initialize form data when user changes
  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        role: user.role || 'basic',
        customer_id: user.customer_id || '',
        status: user.status || 'pending',
        // Additional fields
        phone: user.phone || '',
        company: user.company || '',
        designation: user.designation || '',
        // Metadata
        notes: user.notes || '',
        tags: user.tags || []
      });
    } else {
      // Reset to default values when no user is selected
      setFormData({
        name: '',
        email: '',
        role: 'basic',
        customer_id: '',
        status: 'pending',
        phone: '',
        company: '',
        designation: '',
        notes: '',
        tags: []
      });
    }
  }, [user]);

  const handleFormChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleStatusChange = (newStatus) => {
    setFormData(prev => ({
      ...prev,
      status: newStatus
    }));
  };

  const handleRoleChange = (newRole) => {
    setFormData(prev => ({
      ...prev,
      role: newRole
    }));
  };

  const validateForm = () => {
    if (!formData.name?.trim()) {
      setError('Name is required');
      return false;
    }
    if (!formData.email?.trim()) {
      setError('Email is required');
      return false;
    }
    if (!formData.role) {
      setError('Role is required');
      return false;
    }
    if (!formData.status) {
      setError('Status is required');
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    if (!user || !user.id) {
      setError('No user selected for editing');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const updateData = {
        ...formData,
        // updatedAt: Timestamp.now(), // Removed as per new_code
        // lastModifiedBy: 'admin', // You can get this from AuthContext
        // lastModifiedAt: Timestamp.now() // Removed as per new_code
      };

      // Remove fields that shouldn't be updated
      delete updateData.id;
      delete updateData.uid;
      delete updateData.createdAt;

      const result = await userService.updateUser(user.id, updateData);
      
      if (result.success) {
        setSuccess('User updated successfully!');
        
        // Call callback to refresh parent component
        if (onUserUpdated) {
          onUserUpdated();
        }

        // Auto-hide after 2 seconds
        setTimeout(() => {
          onHide();
        }, 2000);
      } else {
        setError(result.message || 'Failed to update user');
      }

    } catch (error) {
      console.error('Error updating user:', error);
      setError('Failed to update user: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusToggle = (newStatus) => {
    setConfirmAction({
      type: 'status',
      from: formData.status,
      to: newStatus,
      message: `Are you sure you want to change user status from "${formData.status}" to "${newStatus}"?`
    });
    setShowConfirmModal(true);
  };

  const handleRoleToggle = (newRole) => {
    setConfirmAction({
      type: 'role',
      from: formData.role,
      to: newRole,
      message: `Are you sure you want to change user role from "${formData.role}" to "${newRole}"?`
    });
    setShowConfirmModal(true);
  };

  const confirmActionHandler = () => {
    if (confirmAction.type === 'status') {
      handleStatusChange(confirmAction.to);
    } else if (confirmAction.type === 'role') {
      handleRoleChange(confirmAction.to);
    }
    setShowConfirmModal(false);
    setConfirmAction(null);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'approved': { variant: 'success', icon: <CheckCircle />, text: 'Approved' },
      'pending': { variant: 'warning', icon: <Clock />, text: 'Pending' },
      'rejected': { variant: 'danger', icon: <XCircle />, text: 'Rejected' },
      'suspended': { variant: 'secondary', icon: <Ban />, text: 'Suspended' }
    };

    const config = statusConfig[status] || statusConfig['pending'];
    
    return (
      <Badge bg={config.variant} className="d-flex align-items-center gap-1">
        {config.icon}
        {config.text}
      </Badge>
    );
  };

  const getRoleBadge = (role) => {
    const roleConfig = {
      'admin': { variant: 'danger', text: 'Admin' },
      'premium': { variant: 'primary', text: 'Premium' },
      'basic': { variant: 'secondary', text: 'Basic' }
    };

    const config = roleConfig[role] || roleConfig['basic'];
    
    return (
      <Badge bg={config.variant}>
        {config.text}
      </Badge>
    );
  };

  if (!user) return null;

  return (
    <>
      {/* Main Edit Modal */}
      <Modal show={show} onHide={onHide} size="lg" centered className="user-edit-modal">
        <Modal.Header closeButton>
          <Modal.Title>
            <Pencil className="me-2" />
            Edit User: {user?.name || 'Loading...'}
          </Modal.Title>
        </Modal.Header>
        
        <Modal.Body>
          {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}
          {success && <Alert variant="success" onClose={() => setSuccess(null)} dismissible>{success}</Alert>}
          
          <Form>
            {/* Basic Information */}
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleFormChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email *</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleFormChange}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Phone</Form.Label>
                  <Form.Control
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleFormChange}
                    placeholder="+1234567890"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Customer ID</Form.Label>
                  <Form.Control
                    type="text"
                    name="customer_id"
                    value={formData.customer_id}
                    onChange={handleFormChange}
                    placeholder="CUST-001"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Company</Form.Label>
                  <Form.Control
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleFormChange}
                    placeholder="Company Name"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Designation</Form.Label>
                  <Form.Control
                    type="text"
                    name="designation"
                    value={formData.designation}
                    onChange={handleFormChange}
                    placeholder="Job Title"
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Status and Role Management */}
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Current Status</Form.Label>
                  <div className="d-flex align-items-center gap-2 mb-2">
                    {getStatusBadge(formData.status)}
                  </div>
                  <div className="d-flex gap-1 flex-wrap">
                    {formData.status !== 'approved' && (
                      <Button
                        size="sm"
                        variant="outline-success"
                        onClick={() => handleStatusToggle('approved')}
                      >
                        <CheckCircle className="me-1" />
                        Approve
                      </Button>
                    )}
                    {formData.status !== 'pending' && (
                      <Button
                        size="sm"
                        variant="outline-warning"
                        onClick={() => handleStatusToggle('pending')}
                      >
                        <Clock className="me-1" />
                        Set Pending
                      </Button>
                    )}
                    {formData.status !== 'rejected' && (
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => handleStatusToggle('rejected')}
                      >
                        <XCircle className="me-1" />
                        Reject
                      </Button>
                    )}
                    {formData.status !== 'suspended' && (
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={() => handleStatusToggle('suspended')}
                      >
                        <Ban className="me-1" />
                        Suspend
                      </Button>
                    )}
                  </div>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Current Role</Form.Label>
                  <div className="d-flex align-items-center gap-2 mb-2">
                    {getRoleBadge(formData.role)}
                  </div>
                  <div className="d-flex gap-1 flex-wrap">
                    {formData.role !== 'basic' && (
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={() => handleRoleToggle('basic')}
                      >
                        Set Basic
                      </Button>
                    )}
                    {formData.role !== 'premium' && (
                      <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={() => handleRoleToggle('premium')}
                      >
                        Set Premium
                      </Button>
                    )}
                    {formData.role !== 'admin' && (
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => handleRoleToggle('admin')}
                      >
                        Set Admin
                      </Button>
                    )}
                  </div>
                </Form.Group>
              </Col>
            </Row>

            {/* Additional Information */}
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Notes</Form.Label>
                  <Form.Control
                    as="textarea"
                    name="notes"
                    value={formData.notes}
                    onChange={handleFormChange}
                    rows={3}
                    placeholder="Add any notes about this user..."
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* User Information Display */}
            {user && (
              <Row>
                <Col md={12}>
                  <div className="bg-light p-3 rounded">
                    <h6 className="mb-2">User Information</h6>
                    <div className="row">
                      <div className="col-md-6">
                        <small className="text-muted">User ID:</small>
                        <div className="fw-bold">{user.id}</div>
                      </div>
                      <div className="col-md-6">
                        <small className="text-muted">Created:</small>
                        <div className="fw-bold">
                          {user.createdAt ? new Date(user.createdAt.toDate()).toLocaleDateString() : 'N/A'}
                        </div>
                      </div>
                      <div className="col-md-6">
                        <small className="text-muted">Last Updated:</small>
                        <div className="fw-bold">
                          {user.updatedAt ? new Date(user.updatedAt.toDate()).toLocaleDateString() : 'N/A'}
                        </div>
                      </div>
                      <div className="col-md-6">
                        <small className="text-muted">Provider:</small>
                        <div className="fw-bold">{user.provider || 'N/A'}</div>
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
            )}
          </Form>
        </Modal.Body>
        
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? 'Saving...' : (
              <>
                <Save className="me-1" />
                Save Changes
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Confirmation Modal */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} size="sm" centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Action</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {confirmAction?.message}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowConfirmModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={confirmActionHandler}>
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default UserEditModal; 